import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { Card, CardActionArea, CardContent, Grid } from "@mui/material";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import { Typography, Button, CircularProgress, Stack } from "@financeable-com-au/financeable-ui";
import { IDispatchResponse, InsuranceQuoteProps } from "@/utils/interface";
import {
  ERRORS_MESSAGE,
  FILE_AWAITING_MESSAGE,
  INSURANCE_QUOTE_ASSETS,
  INSURANCE_QUOTE_TO,
  SUCCESS_MESSAGE,
} from "@/constants";
import {
  userDetailsSelector,
  createInsuranceQuote,
  sendInsuranceQuote,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import { InsuranceMonthlyPricing, UserDetailsState } from "@/store/slices/types/applicationFormSlice";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { formatToReadableDate } from "@/utils/formatDate";
import { fCurrencyDecimal } from "@/utils/formatNumber";
import MissingRequiredFields from "@/components/CommonComponents/MissingRequiredFields";
import { getInsuranceQuoteMissingFields } from "./insuranceQuoteRequiredFields";

const QuoteDataItem = ({ label, value }: { label: string; value: string }) => {
  return (
    <Grid item xs={12} sm={3}>
      <Typography level="body-md">
        <strong>{label}:</strong> {value}
      </Typography>
    </Grid>
  );
};

/**
 * The monthly option shown under the premium: the instalment amount, then e.g. "/month: 9 monthly instalments after
 * $1,608.12 upfront (total $8,040.33 incl. fees)". Nothing unless it is offered and every amount it states is known.
 */
const getMonthlyPricingSummary = (monthlyPricing: InsuranceMonthlyPricing | undefined) => {
  if (!monthlyPricing?.available) return undefined;

  const { recurringInstalmentAmount, numberOfRecurringInstalments, upfrontAmount, totalPayable } = monthlyPricing;
  if (
    recurringInstalmentAmount === undefined ||
    upfrontAmount === undefined ||
    totalPayable === undefined ||
    !numberOfRecurringInstalments
  ) {
    return undefined;
  }

  const plural = numberOfRecurringInstalments === 1 ? "" : "s";
  const upfront = upfrontAmount > 0 ? ` after ${fCurrencyDecimal(upfrontAmount)} upfront` : "";
  const total = `(total ${fCurrencyDecimal(totalPayable)} incl. fees)`;
  return {
    instalmentAmount: fCurrencyDecimal(recurringInstalmentAmount),
    terms: `/month: ${numberOfRecurringInstalments} monthly instalment${plural}${upfront} ${total}`,
  };
};

const InsuranceQuote = ({ asset, customer, applicationId }: InsuranceQuoteProps) => {
  const dispatch = useDispatch();
  const { gettingInsuranceQuote, sendingInsuranceQuote, application } = useSelector(
    userDetailsSelector,
  ) as UserDetailsState;
  const firstEntity = application?.entities?.[0];
  const missingRequiredFields = getInsuranceQuoteMissingFields({ application, asset, customer });
  const isGettingInsuranceQuote = Boolean(gettingInsuranceQuote[asset._id]);

  const disableInsuranceQuoteButton = missingRequiredFields.length > 0 || isGettingInsuranceQuote;
  const isCustomerSendingInsuranceQuote =
    sendingInsuranceQuote.loading && sendingInsuranceQuote.to === INSURANCE_QUOTE_TO.CUSTOMER ? true : false;
  const isBrokerSendingInsuranceQuote =
    sendingInsuranceQuote.loading && sendingInsuranceQuote.to === INSURANCE_QUOTE_TO.BROKER ? true : false;
  const isInsuranceQuoteVisible = asset?.assetType ? INSURANCE_QUOTE_ASSETS.includes(asset?.assetType) : true;
  const insuranceQuote = asset.insuranceQuote;
  const monthlyPricingSummary = getMonthlyPricingSummary(insuranceQuote?.monthlyPricing);

  const handle = {
    createInsuranceQuote: () => {
      const getInsuranceQuotePayload = {
        assetId: asset._id,
        customerId: customer._id,
        entityId: firstEntity?._id,
        applicationId,
      };
      dispatch(createInsuranceQuote(getInsuranceQuotePayload)).then((res: IDispatchResponse) => {
        if (res?.error) {
          dispatch(
            openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.insuranceQuoteCreated,
              intent: "success",
            }),
          );
        }
      });
    },
    sendInsuranceQuote: (to: (typeof INSURANCE_QUOTE_TO)[keyof typeof INSURANCE_QUOTE_TO]) => {
      if (!insuranceQuote) return;
      dispatch(
        sendInsuranceQuote({
          quoteGuid: insuranceQuote.quoteGuid,
          assetId: asset._id,
          applicationId,
          to,
        }),
      ).then((res: IDispatchResponse) => {
        if (res?.error) {
          dispatch(
            openSnackbar({ message: res?.payload?.status_message || ERRORS_MESSAGE.fetchErrorMsg, intent: "danger" }),
          );
        } else {
          dispatch(
            openSnackbar({
              message: res?.payload?.data?.status_message || SUCCESS_MESSAGE.insuranceQuoteSentToCustomer,
              intent: "success",
            }),
          );
        }
      });
    },
  };

  return (
    <React.Fragment>
      {insuranceQuote && (
        <Grid container item direction="row" spacing={1} m="10px 0">
          <Grid container item direction="row" spacing={1} sm={12} xs={12}>
            <Grid item sm={6} xs={12}>
              <Typography level="body-lg" fontWeight={600}>
                Estimated premium: {fCurrencyDecimal(Number(insuranceQuote.estimatedPremium) || 0)}/annually
              </Typography>
            </Grid>
            <Grid item sm={6} xs={12}>
              <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                {(insuranceQuote.allowSendToCustomer && !insuranceQuote?.customerQuoteLink) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handle.sendInsuranceQuote(INSURANCE_QUOTE_TO.CUSTOMER)}
                    disabled={isCustomerSendingInsuranceQuote}
                  >
                    {isCustomerSendingInsuranceQuote ? "Sending..." : "Send to Customer"}{" "}
                    <SendOutlinedIcon fontSize="small" style={{ marginLeft: "3px" }} />
                  </Button>
                )}
                {(insuranceQuote.allowSendToBroker && !insuranceQuote?.brokerQuoteLink) && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => handle.sendInsuranceQuote(INSURANCE_QUOTE_TO.BROKER)}
                    disabled={isBrokerSendingInsuranceQuote}
                  >
                    {isBrokerSendingInsuranceQuote ? "Sending..." : "Send to Broker"}{" "}
                    <SendOutlinedIcon fontSize="small" style={{ marginLeft: "3px" }} />
                  </Button>
                )}
              </Stack>
            </Grid>
            {monthlyPricingSummary && (
              <Grid item xs={12}>
                <Typography level="body-md">
                  or{" "}
                  <Typography component="span" textColor="primary.200" fontWeight={600}>
                    {monthlyPricingSummary.instalmentAmount}
                  </Typography>
                  {monthlyPricingSummary.terms}
                </Typography>
              </Grid>
            )}
          </Grid>
          <Grid container item direction="row" spacing={1} mt="30px">
            <QuoteDataItem label="Quote Id" value={insuranceQuote.quoteId} />
            <QuoteDataItem
              label="Period of cover"
              value={
                typeof insuranceQuote.periodOfCover === "string"
                  ? insuranceQuote.periodOfCover
                  : `${insuranceQuote.periodOfCover} months`
              }
            />
            <QuoteDataItem
              label="Inception date"
              value={insuranceQuote.inceptionDate ? formatToReadableDate(insuranceQuote.inceptionDate) : ""}
            />
            <QuoteDataItem
              label="Expiry date"
              value={insuranceQuote.expiryDate ? formatToReadableDate(insuranceQuote.expiryDate) : ""}
            />
          </Grid>
        </Grid>
      )}
      {!insuranceQuote && isInsuranceQuoteVisible && (
        <Grid container item xs={12} spacing={1}>
          <Grid item md={12} sm={12} mb="30px">
            <Card
              style={{ height: "100%" }}
              onClick={() => {
                if (disableInsuranceQuoteButton) return;
                handle.createInsuranceQuote();
              }}
            >
              <CardActionArea
                disableRipple={disableInsuranceQuoteButton ? true : false}
                style={{
                  height: "100%",
                  display: "flex",
                  alignContent: "start",
                  cursor: disableInsuranceQuoteButton ? "auto" : "",
                  opacity: disableInsuranceQuoteButton ? "0.6" : "",
                  backgroundColor: disableInsuranceQuoteButton ? "#f9f9f9" : "",
                }}
              >
                <CardContent
                  style={{
                    textAlign: "center",
                    padding: "1px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                  }}
                >
                  <TaskOutlinedIcon fontSize="medium" color={disableInsuranceQuoteButton ? "secondary" : "error"} />
                  <Typography fontSize={20} level="title-md">
                    Get Insurance Quote
                  </Typography>
                </CardContent>
                {isGettingInsuranceQuote && (
                  <Stack
                    style={{
                      background: "rgba(0,0,0, 0.5)",
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      top: 0,
                      right: 0,
                      left: 0,
                      bottom: 0,
                      color: "#fff",
                    }}
                  >
                    <CircularProgress size="md" />
                  </Stack>
                )}
              </CardActionArea>
            </Card>
            <Grid container direction="row" xs={12} sm={12} md={12} style={{ width: "100%" }}>
              <Grid item xs={8} sm={8} md={8} mt={1}>
                {isGettingInsuranceQuote && (
                  <Typography level="body-xs">{FILE_AWAITING_MESSAGE.gettingInsuranceQuoteDataMsg}</Typography>
                )}
                {!isGettingInsuranceQuote && <MissingRequiredFields fields={missingRequiredFields} />}
              </Grid>
              <Grid item xs={4} sm={4} md={4}>
                <Stack
                  direction="row"
                  style={{
                    alignItems: "center",
                    justifyContent: "end",
                  }}
                >
                  <Typography level="body-xs">Powered by</Typography>
                  <img height="30px" src="/static/your-insurance.png" alt="" />
                </Stack>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
      {asset?.assetType && !isInsuranceQuoteVisible && (
        <Grid container item xs={12} spacing={1}>
          <Grid item md={12} sm={12} mb="30px">
            <Typography level="body-lg" fontWeight={600} textAlign="center">
              Insurance quote is not available for this asset type
            </Typography>
          </Grid>
        </Grid>
      )}
    </React.Fragment>
  );
};

export default InsuranceQuote;
